import { ActorRefFrom, AnyActorLogic, DoneActorEvent } from 'xstate';
import z from 'zod';
import { EmployerFacilityValues } from '~/features/market/facility/types';
import { optionSchema } from '~/features/types';
import { facilityMachine } from '~/features/vault';
import { FacilityParentEvents } from '~/features/vault/facility/events';
import { EmployerFacility } from '~/types/hlx/employerFacility';
import { MsaasJobListingFacility } from '~/types/hlx/msaasJobListingFacility';

export interface Context {
  facilityRef: ActorRefFrom<typeof facilityMachine>;
  facility: { facilityName: string; facilityId: number } | undefined;
  employerFacility: EmployerFacility | undefined;
  msaasJobListingFacilities: MsaasJobListingFacility[];
  msaasJobListingFacilitiesSubmitted: boolean;
  states: string[];
  // [MCV-1523] Captured on submit so it can be handed back to the parent
  // listing; only the job-listing flow persists it (msaas has no primary).
  primaryFacility?: number;
  facilitiesToDelete: number[];
  loading: boolean;
  modal: boolean;
  listingId: number;

  parentRef?: ActorRefFrom<AnyActorLogic>;
}

export type OnAddFacilitiesEvent = {
  type: 'ON_ADD';
  payload: { values: number[] };
};

export type OnUpdateStatesEvent = {
  type: 'ON_UPDATE_STATES';
  payload: { values: string[] };
};

export type OnDeleteFacilitiesEvent = {
  type: 'ON_DELETE';
  payload: { values: number[] };
};

export type OnSubmitEvent = {
  type: 'ON_SUBMIT';
  payload: {
    facilities: number[];
    primaryFacility?: number;
    states: string[];
    shouldPersistStates: boolean;
  };
};

export type OnLoadEvent = {
  type: 'ON_LOAD';
  payload: { listingId: number };
};

export type OnUpdateFacilitiesEvent = {
  type: 'ON_UPDATE_FACILITIES';
};

export type OnNewFacilitySubmitEvent = {
  type: 'ON_NEW_FACILITY_SUBMIT';
  payload: { data: EmployerFacilityValues };
};

export type OnFacilityCreatedEvent = {
  type: 'ON_FACILITY_CREATED';
  payload: { facility: { facilityName: string; facilityId: number } };
};

export type OnAddNewFacilityEvent = {
  type: 'ON_ADD_NEW_FACILITY';
};

export type OnCloseModalEvent = {
  type: 'ON_CLOSE_MODAL';
};

export type SubmitDoneEvent = DoneActorEvent<string[], string>;

export type Events =
  | OnAddFacilitiesEvent
  | OnUpdateStatesEvent
  | OnDeleteFacilitiesEvent
  | OnSubmitEvent
  | OnAddNewFacilityEvent
  | OnCloseModalEvent
  | OnLoadEvent
  | OnNewFacilitySubmitEvent
  | OnFacilityCreatedEvent
  | OnUpdateFacilitiesEvent
  | SubmitDoneEvent
  | FacilityParentEvents;

// [MCV-1546] The facility's state has to survive validation to be usable at
// submit time: zodResolver hands handleSubmit the PARSED object, and a plain
// z.object strips unknown keys, so a `stateCode` hung off the react-select
// option would be gone before onSubmitSuccess ever saw it.
export const facilityOptionSchema = optionSchema.extend({
  stateCode: z.string().optional(),
});

export type FacilityOption = z.infer<typeof facilityOptionSchema>;

const BaseFacilitiesFormSchema = z
  .object({
    facilities: z.array(facilityOptionSchema),
    states: z.array(optionSchema),
    primaryFacility: optionSchema.optional(),
  })
  .superRefine((data, ctx) => {
    if (data.facilities.length === 0 && data.states.length === 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'Either facilities or states must be selected',
        path: ['facilities'],
      });
      ctx.addIssue({
        code: 'custom',
        message: 'Either facilities or states must be selected',
        path: ['states'],
      });
    }
  });

export const FacilitiesFormSchema = BaseFacilitiesFormSchema;

export const JobListingFacilitiesFormSchema = BaseFacilitiesFormSchema.refine(
  (data) => {
    return data?.facilities.length === 0 || !!data?.primaryFacility?.value;
  },
  {
    message: 'Primary facility must be set if facilities are selected',
    path: ['primaryFacility'],
  }
);

// [MCV-1956] A Remote listing has no place of work to pick a facility for, so
// its step only collects the states a provider must be licensed in. Facilities
// are not part of the shape at all, which keeps a stale hydrated value from
// ever failing (or passing) validation on a form that does not show them.
export const RemoteFacilitiesFormSchema = z.object({
  states: z.array(optionSchema).min(1, 'Select at least one state'),
});

export type Scope = {
  context: Context;
  event: Events;
};

export type FacilitiesFormValues = z.infer<typeof FacilitiesFormSchema>;
